import {
  DropdownMenuProps,
  DropdownProps,
  Dropdown as HeroDropdown,
  DropdownMenu as HeroDropdownMenu,
} from '@heroui/react';
import { forwardRef, HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

import { OVERLAY_MOTION_PROPS } from '../constants/motion';
import { HeroBaseProps } from '../utils';

export const Dropdown = (
  props: HeroBaseProps<
    DropdownProps,
    | 'placement'
    | 'shouldBlockScroll'
    | 'shouldCloseOnScroll'
    | 'closeOnSelect'
    | 'offset'
    | 'crossOffset'
    | 'isOpen'
    | 'isDisabled'
  >,
) => (
  <HeroDropdown
    triggerScaleOnOpen={false}
    motionProps={OVERLAY_MOTION_PROPS}
    {...props}
    classNames={{
      base: '[&_[data-slot="base"]]:overflow-visible',
      content: 'rounded-xl shadow-lg border border-secondary-300 p-0',
    }}
  />
);

type DropdownMenuSize = 'sm' | 'md';

export const DropdownMenu = ({
  size = 'sm',
  classNames,
  ...props
}: HeroBaseProps<
  DropdownMenuProps,
  'closeOnSelect' | 'disabledKeys' | 'emptyContent' | 'onAction' | 'onClose' | 'topContent'
> & {
  /** Item density: `sm` (narrowed, default) or `md` (wide). */
  size?: DropdownMenuSize;
}) => (
  <HeroDropdownMenu
    {...props}
    itemClasses={{
      // HeroUI's default item hover uses its own (cool) `default` scale, which isn't mapped to the
      // Sortlist palette — pin it to an on-palette warm neutral on the base slot (where HeroUI paints it).
      base: twMerge(
        size === 'md' ? 'rounded-xl gap-3 px-2.5 py-3' : 'rounded-lg gap-2 px-2 py-1.5',
        'data-[hover=true]:!bg-secondary-100',
      ),
      // Resting text color — HeroUI only colors items on hover/focus, so `href` items would otherwise
      // inherit the global blue anchor color at rest.
      title: 'text-sm leading-[initial] [&_p]:text-sm text-secondary-900',
      description: 'text-secondary-500 group-hover:!text-secondary-500',
      wrapper: 'w-auto min-w-0 flex-1',
    }}
    classNames={{
      base: twMerge('px-1.5 py-2 max-w-[15rem]', classNames?.base),
      list: twMerge(
        '!list-none m-0 p-0 [&_ul]:!pl-0 [&_li[role="presentation"]:last-child]:!mb-0 [&_[data-slot="heading"]]:text-xs [&_[data-slot="heading"]]:font-medium [&_[data-slot="heading"]]:text-secondary-500 [&_li[role="separator"]]:w-[calc(100%+12px)] [&_li[role="separator"]]:ml-[-6px] [&_li[role="separator"]]:list-none [&_li[data-disabled="true"]]:!text-secondary-400 [&_li[data-hover=true]]:!bg-secondary-500/5 [&_li[role="menuitem"]>:not([data-slot="title"]):not([data-slot="wrapper"])]:shrink-0',
        size === 'md' ? '[&_[data-slot="heading"]]:px-2.5' : '[&_[data-slot="heading"]]:px-2',
        classNames?.list,
      ),
    }}
  />
);

/**
 * Adds a transparent overlay over the trigger so composite content (cards, rows
 * with nested buttons) reliably opens the menu instead of inner elements eating
 * the click. The overlay sits above the content, so it also blocks the content's
 * own `:hover` — pass `withOverlay={false}` for a single interactive trigger
 * (e.g. a `SidebarItem`) that should keep its hover styles.
 */
interface DropdownTriggerWrapperProps extends HTMLAttributes<HTMLDivElement> {
  withOverlay?: boolean;
}

export const DropdownTriggerWrapper = forwardRef<HTMLDivElement, DropdownTriggerWrapperProps>(
  ({ children, className, withOverlay = true, ...rest }, ref) => {
    return (
      <div {...rest} ref={ref} className={twMerge('inline-block relative cursor-pointer', className)}>
        {withOverlay ? <div className="absolute top-0 left-0 z-10 w-full h-full" /> : null}
        <div className="relative z-0">{children}</div>
      </div>
    );
  },
);

export { DropdownItem, DropdownTrigger, DropdownSection } from '@heroui/react';
